import {
  availabilitySummary,
  getAvailabilityCalendar,
} from "@/services/naaviApi/internalService";
import { getDoctorClasses } from "@/services/naaviApi/classesService";
import { extractTenantId } from "@/helpers/tenantHelper";
import { toStartOfDayIso } from "@/helpers/dateHelper";
import { mapAvailabilitySummariesToDoctors } from "@/helpers/availabilitySummaryMapper";
import { normalizeClassPrice } from "@/helpers/classPriceHelper";
import {
  normalizeCalendarResponse,
  cleanCalendarParams,
} from "@/helpers/availabilityCalendarHelper";
import type {
  DoctorAvailability,
  DoctorBookingOption,
  DoctorCenter,
} from "@/helpers/availabilitySummaryMapper";
import type { ClassSummary } from "@/services/naaviApi/classesService";

type DoctorAvailabilityWithVersion = DoctorAvailability & {
  scheduleVersion?: number;
};

type DoctorBookingOptionWithVersion = DoctorBookingOption & {
  scheduleVersion?: number;
};
import type {
  ResourceScheduleDay,
  ResourceScheduleSlot,
} from "@/services/naaviApi/types";

const SEARCH_TYPE_FULL_DAY = 2;
const DEFAULT_CALENDAR_DAYS = 4;

const DEFAULT_TRANSLATE = (key: string) => key;

type AddressAccumulator = {
  id: string;
  name: string;
  location: string;
  schedule: Array<{ date: string; times: string[] }>;
};

interface FetchDoctorProfileParams {
  doctorId: number;
  tenantId?: string | null;
  tenantXId?: string | null;
  therapy?: string | null;
  therapyType?: string | null;
  // When known (e.g. after a /professional call) this filters the availability search to
  // a single resource server-side — much smaller payload than the whole tenant listing.
  resourceId?: number | null;
}

interface FetchDoctorProfileOptions {
  translate?: (key: string) => string;
  prefetchCalendars?: boolean;
}

export async function fetchDoctorProfile(
  params: FetchDoctorProfileParams,
  options: FetchDoctorProfileOptions = {},
) {
  const {
    doctorId,
    tenantId = null,
    therapy = null,
    therapyType = null,
    resourceId = null,
  } = params;

  const translate = options.translate ?? DEFAULT_TRANSLATE;
  const prefetchCalendars = options.prefetchCalendars === true;

  const baseDate = new Date();
  baseDate.setHours(0, 0, 0, 0);
  const baseDateIso = toStartOfDayIso(baseDate);

  const availabilityPayload: Record<string, unknown> = {
    date: baseDateIso,
    concept: 0,
    searchType: SEARCH_TYPE_FULL_DAY,
  };

  availabilityPayload.tenantIds = [tenantId];
  // Server-side resource filter — keeps payload small for the profile view without affecting
  // the tenant-wide search listing (which never passes resourceId).
  if (resourceId) {
    availabilityPayload.resourceId = resourceId;
  }

  const availabilityResponse = await availabilitySummary(availabilityPayload);

  const summaries = Array.isArray(availabilityResponse?.items)
    ? availabilityResponse.items
    : [];

  const mappedDoctors = mapAvailabilitySummariesToDoctors(summaries, {
    baseDateIso,
    translate,
  });

  const targetDoctor =
    mappedDoctors.find((entry) => {
      const numericDoctorId = Number(entry.id);
      const summaryUserId = Number(entry.rawSummary?.userId);
      const summaryResourceId = Number(entry.resourceId);

      if (
        Number.isFinite(numericDoctorId) &&
        numericDoctorId === Number(doctorId)
      ) {
        return true;
      }
      if (
        Number.isFinite(summaryUserId) &&
        summaryUserId === Number(doctorId)
      ) {
        return true;
      }
      if (
        Number.isFinite(summaryResourceId) &&
        summaryResourceId === Number(doctorId)
      ) {
        return true;
      }
      return false;
    }) ?? mappedDoctors[0];

  if (!targetDoctor) {
    throw new Error("Doctor not found in availability response");
  }

  const classTenantId = targetDoctor.tenantId;
  let doctorClasses: ClassSummary[] = [];
  if (classTenantId) {
    const resolvedDoctorId = Number.isFinite(Number(targetDoctor.id))
      ? Number(targetDoctor.id)
      : null;
    const resolvedResourceId = Number.isFinite(Number(targetDoctor.resourceId))
      ? Number(targetDoctor.resourceId)
      : null;

    const doctorCandidates = [resolvedDoctorId, resolvedResourceId]
      .filter((candidate): candidate is number => Number.isFinite(candidate))
      .map((candidate) => Number(candidate));

    if (doctorCandidates.length) {
      const tenantCandidates = Array.from(
        [classTenantId, targetDoctor.tenantXId]
          .filter(
            (candidate): candidate is string =>
              typeof candidate === "string" && candidate.trim().length > 0,
          )
          .reduce((acc, candidate) => {
            const trimmed = candidate.trim();
            if (!trimmed) {
              return acc;
            }
            acc.add(trimmed);
            const canonical = extractTenantId(trimmed);
            if (canonical && canonical !== trimmed) {
              acc.add(canonical);
            }
            return acc;
          }, new Set<string>()),
      );

      let classesFound = false;
      const capturedErrors: unknown[] = [];

      for (const tenantCandidate of tenantCandidates) {
        if (classesFound) {
          break;
        }
        for (const doctorCandidate of doctorCandidates) {
          try {
            const doctorClassesResponse = await getDoctorClasses(
              tenantCandidate,
              doctorCandidate,
              baseDateIso,
            );
            const apiClasses = Array.isArray(doctorClassesResponse?.items)
              ? (doctorClassesResponse.items as ClassSummary[])
              : [];

            if (apiClasses.length) {
              doctorClasses = apiClasses
                .map((classItem) => normalizeClassPrice(classItem))
                .sort((a, b) => {
                  const aDate = Date.parse(a?.start ?? "");
                  const bDate = Date.parse(b?.start ?? "");
                  if (Number.isNaN(aDate) && Number.isNaN(bDate)) return 0;
                  if (Number.isNaN(aDate)) return 1;
                  if (Number.isNaN(bDate)) return -1;
                  return aDate - bDate;
                });
              classesFound = true;
              break;
            }
          } catch (error) {
            capturedErrors.push({
              tenantCandidate,
              doctorCandidate,
              error,
            });
          }
        }
      }
    }
  }

  const tenantKey = targetDoctor.tenantXId || targetDoctor.tenantId || null;

  if (tenantKey && prefetchCalendars) {
    await loadCalendarsForDoctor(targetDoctor, {
      tenantKey,
      baseDateIso,
      therapy,
      therapyType,
      days: DEFAULT_CALENDAR_DAYS,
    });
  }

  targetDoctor.classes = doctorClasses;

  const addressesInfo = buildAddressesFromBookingOptions(
    targetDoctor,
    translate,
  );
  targetDoctor.totalDatesMax = addressesInfo.maxDates;
  targetDoctor.totalTimesMax = addressesInfo.maxTimes;

  const doctorData = composeDoctorFromAvailability(
    targetDoctor,
    doctorClasses,
    addressesInfo,
  );

  return {
    doctor: doctorData,
    schedule: targetDoctor,
    classes: doctorClasses,
    mapDoctors: doctorData?.user ? [doctorData.user] : [],
  };
}

type CalendarContext = {
  tenantKey: string;
  baseDateIso: string;
  therapy?: string | null;
  therapyType?: string | null;
  days?: number;
};

async function loadCalendarsForDoctor(
  doctor: DoctorAvailabilityWithVersion,
  context: CalendarContext,
) {
  if (!doctor || !context.tenantKey) {
    return;
  }

  const bookingOptions = Array.isArray(doctor.bookingOptions)
    ? [...doctor.bookingOptions]
    : [];
  if (!bookingOptions.length) {
    doctor.bookingOptions = bookingOptions;
    doctor.totalDatesMax = doctor.totalDatesMax ?? 0;
    doctor.totalTimesMax = doctor.totalTimesMax ?? 0;
    return;
  }

  let maxDates = doctor.totalDatesMax ?? 0;
  let maxTimes = doctor.totalTimesMax ?? 0;
  let earliestBaseIso = doctor.baseDateIso ?? context.baseDateIso ?? null;

  for (let index = 0; index < bookingOptions.length; index += 1) {
    const originalOption = bookingOptions[
      index
    ] as DoctorBookingOptionWithVersion;

    const option: DoctorBookingOptionWithVersion = {
      ...originalOption,
      scheduleLoading: true,
      scheduleError: null,
    };

    const params: Record<string, string | number | boolean | undefined> = {
      date: context.baseDateIso,
      searchType: SEARCH_TYPE_FULL_DAY,
      concept: 0,
      days: context.days ?? DEFAULT_CALENDAR_DAYS,
    };

    if (context.therapy) {
      params.therapy = context.therapy;
    }
    if (context.therapyType) {
      params.therapyType = context.therapyType;
    }

    const cleanedParams = cleanCalendarParams(params);

    try {
      const calendar = await getAvailabilityCalendar(
        context.tenantKey,
        doctor.resourceId,
        option.bookingTypeId,
        cleanedParams,
      );

      const calendarResult = normalizeCalendarResponse({
        response: calendar,
        previousSchedule: Array.isArray(option.schedule)
          ? (option.schedule as ResourceScheduleDay[])
          : [],
        merge: false,
        currentPriceLocalized: option.priceLocalized,
        currentPriceLabel: option.price ?? null,
        fallbackBaseDateIso:
          typeof cleanedParams.date === "string" ? cleanedParams.date : null,
      });

      option.schedule = calendarResult.schedule;
      option.hours = calendarResult.hours;
      const resolvedPriceLabel =
        calendarResult.priceLabel !== null &&
        calendarResult.priceLabel !== undefined
          ? String(calendarResult.priceLabel)
          : null;
      option.price = resolvedPriceLabel || option.price || "";
      option.priceLocalized =
        calendarResult.priceLocalized ?? option.priceLocalized ?? null;
      option.scheduleLoaded = true;
      option.scheduleLoading = false;
      option.scheduleError = null;

      const effectiveDates =
        calendarResult.totals.dates || calendarResult.schedule.length || 0;
      const effectiveTimes = Math.max(
        calendarResult.totals.times || 0,
        calendarResult.scheduleMaxTimes,
      );

      maxDates = Math.max(maxDates, effectiveDates);
      maxTimes = Math.max(maxTimes, effectiveTimes);

      const windowStart = calendarResult.schedule.length
        ? calendarResult.schedule[0].date
        : null;
      if (windowStart) {
        const startIso = toStartOfDayIso(`${windowStart}T00:00:00`);
        if (!earliestBaseIso || startIso < earliestBaseIso) {
          earliestBaseIso = startIso;
        }
      } else if (
        cleanedParams.date &&
        (!earliestBaseIso || String(cleanedParams.date) < earliestBaseIso)
      ) {
        earliestBaseIso = String(cleanedParams.date);
      }

      const currentVersion =
        Number((doctor as DoctorAvailabilityWithVersion).scheduleVersion) || 0;
      const nextVersion = currentVersion + 1;
      (doctor as DoctorAvailabilityWithVersion).scheduleVersion = nextVersion;
      option.scheduleVersion = nextVersion;
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);

      option.schedule = Array.isArray(option.schedule)
        ? (option.schedule as ResourceScheduleDay[])
        : [];
      option.hours = Array.isArray(option.hours) ? option.hours : [];
      option.scheduleLoaded = false;
      option.scheduleLoading = false;
      option.scheduleError = message || "unavailable";
    }

    bookingOptions[index] = option;
  }

  doctor.bookingOptions = bookingOptions;
  doctor.totalDatesMax = maxDates;
  doctor.totalTimesMax = maxTimes;
  doctor.baseDateIso = earliestBaseIso ?? context.baseDateIso ?? null;
}

function buildAddressesFromBookingOptions(
  doctor: DoctorAvailability,
  translate: (key: string) => string,
) {
  const registry = new Map<string, AddressAccumulator>();

  const clinicLabel = translate("Clinic");
  const centers: DoctorCenter[] = Array.isArray(doctor.centers)
    ? doctor.centers
    : [];
  const bookingOptions = Array.isArray(doctor.bookingOptions)
    ? doctor.bookingOptions
    : [];

  bookingOptions.forEach((option) => {
    const schedule = Array.isArray(option.schedule)
      ? (option.schedule as ResourceScheduleDay[])
      : [];
    if (!schedule.length) {
      return;
    }

    const key =
      option.centerKey ??
      (option.centerId != null ? String(option.centerId) : String(option.key));
    const center =
      centers.find((item) => String(item.key) === String(key)) ??
      centers[0] ??
      null;

    const name =
      option.location?.name ?? center?.name ?? doctor.tenantName ?? clinicLabel;
    const address =
      option.location?.address ??
      center?.address ??
      doctor.mainAddress ??
      doctor.rawSummary?.tenantAddress ??
      "";

    let entry = registry.get(key);
    if (!entry) {
      entry = {
        id: String(key),
        name,
        location: address,
        schedule: [],
      };
      registry.set(key, entry);
    }

    const accumulator = entry;

    schedule.forEach((day) => {
      if (!day || !day.date) {
        return;
      }

      let dayEntry = accumulator.schedule.find(
        (item) => item.date === day.date,
      );
      if (!dayEntry) {
        dayEntry = { date: day.date, times: [] as string[] };
        accumulator.schedule.push(dayEntry);
      }

      const dayTimes = dayEntry.times;

      const times = Array.isArray(day.times) ? day.times : [];
      times.forEach((time) => {
        if (typeof time === "string" && !dayTimes.includes(time)) {
          dayTimes.push(time);
        }
      });

      const slots = Array.isArray(day.slots)
        ? (day.slots as ResourceScheduleSlot[])
        : [];
      slots.forEach((slot) => {
        const slotTime = slot?.time ?? null;
        if (slotTime && !dayTimes.includes(slotTime)) {
          dayTimes.push(slotTime);
        }
      });
    });
  });

  const addresses = Array.from(registry.values()).map((entry) => {
    const sortedSchedule = entry.schedule
      .map(({ date, times }) => ({ date, times: [...new Set(times)].sort() }))
      .sort((a, b) => a.date.localeCompare(b.date));
    return {
      ...entry,
      schedule: sortedSchedule,
    };
  });

  const maxDates = addresses.reduce(
    (acc, item) => Math.max(acc, item.schedule.length),
    0,
  );
  const maxTimes = addresses.reduce(
    (acc, item) =>
      Math.max(
        acc,
        item.schedule.reduce(
          (inner, day) => Math.max(inner, day.times.length),
          0,
        ),
      ),
    0,
  );

  return { addresses, maxDates, maxTimes };
}

function composeDoctorFromAvailability(
  doctor: DoctorAvailability,
  classes: ClassSummary[],
  addressesInfo: {
    addresses: AddressAccumulator[];
    maxDates: number;
    maxTimes: number;
  },
) {
  const primaryCenter =
    Array.isArray(doctor.centers) && doctor.centers.length
      ? doctor.centers[0]
      : null;

  const image = doctor.rawSummary?.userAvatarUrl ?? doctor.image ?? null;

  const consultationFee =
    doctor.consultationFee ??
    doctor.bookingOptions?.find((option) => Boolean(option?.price))?.price ??
    null;

  const user = {
    id: Number(doctor.id ?? doctor.resourceId),
    resourceId: doctor.resourceId,
    slug: doctor.slug ?? null,
    image,
    logo: image,
    name: doctor.name ?? doctor.rawSummary?.userName ?? "",
    specialty: doctor.specialty ?? doctor.rawSummary?.userSpecialty ?? "",
    appointmentDate: null,
    mainAddress:
      doctor.mainAddress ??
      primaryCenter?.address ??
      doctor.rawSummary?.tenantAddress ??
      "",
    paymentMethod: doctor.paymentMethod ?? "",
    consultationFee,
    personalizedService: true,
    position: doctor.position ?? null,
    services: Array.isArray(doctor.services) ? doctor.services : [],
    aboutMe: Array.isArray(doctor.aboutMe) ? doctor.aboutMe : [],
    education: Array.isArray(doctor.education) ? doctor.education : [],
    tenantId: doctor.tenantId ?? null,
    tenantXId: doctor.tenantXId ?? null,
    tenantName: doctor.tenantName ?? "",
    bookingOptions: doctor.bookingOptions,
    centers: doctor.centers,
    addresses: addressesInfo.addresses,
    totalDatesMax: addressesInfo.maxDates,
    totalTimesMax: addressesInfo.maxTimes,
    classes,
    distanceKm: doctor.distanceKm ?? null,
  };

  return {
    user,
    reviews: [],
    tenant: {
      id: doctor.tenantId ?? null,
      xId: doctor.tenantXId ?? null,
      name: doctor.tenantName ?? "",
      url: doctor.tenantUrl ?? null,
    },
  };
}

export function getDoctorProfileFallback() {
  return {
    doctor: null,
    schedule: null,
    classes: [],
    mapDoctors: [],
  };
}
